import { useQuery } from '@apollo/client';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as R from 'ramda';
import { Link } from 'react-router';
import {
  BookmarkParentType,
  BookmarkTargetType,
} from '~/apollo/generated/v3/graphql';
import { graphql } from '~/apollo/generated/v4';
import type { StudyPartsFragment } from '~/apollo/generated/v4/graphql';
import { NotFound } from '~/components/common/NotFound';
import { Panel } from '~/components/common/Panel';
import { PublishedContainer } from '~/components/common/published-container';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { PanoramaItemDisplayV4 } from '~/components/supporting-object/panorama-item-display-v4';
import { GigaPanViewer } from '~/components/supporting-object/giga-pan-viewer';
import { useRouteParam } from '~/hooks/routing';
import {
  outcropPanoramasTabRoute,
  studyPanoramasTabRoute,
  studyRoute,
} from '~/paths';
import { createRefetchQueries } from '~/utils/graphql';
import { isPublished } from '~/utils/modules/supporting-object';

const OUTCROP_PANORAMAS_ROUTE = graphql(`
  query OutcropPanoramasRoute($id: ID!) {
    outcropGet(id: $id) {
      ...outcropParts
      gigaPans {
        ...gigaPanParts
        pictures {
          ...pictureParts
          pictureUrl
          thumbnailUrl: pictureUrl(width: 500)
          file {
            ...fileParts
          }
        }
      }

      studies {
        ...studyParts
        gigaPans {
          ...gigaPanParts
          pictures {
            ...pictureParts
            pictureUrl
            thumbnailUrl: pictureUrl(width: 500)
            file {
              ...fileParts
            }
          }
        }
      }
    }
  }
`);

export default function OutcropPanoramasRoute() {
  const outcropId = useRouteParam('outcropId', parseInt);

  const { data, loading } = useQuery(OUTCROP_PANORAMAS_ROUTE, {
    variables: { id: outcropId },
  });

  const refetchQueries = createRefetchQueries([
    { query: OUTCROP_PANORAMAS_ROUTE, variables: { id: outcropId } },
  ]);

  const outcrop = data?.outcropGet;

  if (loading) return <SpinnerPlaceholder />;
  if (!outcrop) return <NotFound />;

  type Outcrop = typeof outcrop;
  type Study = Outcrop['studies'][number];

  const outcropGigaPans = R.sortBy(
    gp => gp.priority ?? Infinity,
    outcrop.gigaPans,
  );

  const studyGigaPans = (study: Study) =>
    study.gigaPans
      .filter(gp => gp.outcropTagId === outcropId)
      .sort(R.ascend(gp => gp.priority ?? Infinity));

  const studies = R.pipe(
    () => outcrop?.studies ?? [],
    studies => studies.filter(s => studyGigaPans(s).length > 0),
    R.sortBy(study => study.name),
  )();

  const gigaPanPictures = (gigaPan: Outcrop['gigaPans'][number]) =>
    R.pipe(
      () => gigaPan.pictures,
      R.sortBy(picture => picture.priority ?? Infinity),
    )();

  // See if any panos (or tagged panos) have a gigaPanHash set
  const hasAnyGigaPans = studies.some(study =>
    study.gigaPans.some(gp => !!gp.gigaPanHash),
  );

  return (
    <div className="space-y-4">
      {hasAnyGigaPans && (
        <p>
          Note: Please be aware that the Gigapans load extremely slowly because
          they are embedded externally from the Gigapan website. A better
          hosting solution based on the Safari database is under discussion.
        </p>
      )}

      {outcropGigaPans.map(gigaPan => (
        <div key={gigaPan.id}>
          <PublishedContainer published={isPublished(gigaPan)}>
            <GigaPanViewer
              hash={gigaPan.gigaPanHash}
              bookmarkable={{
                parentType: BookmarkParentType.Outcrop,
                parentId: outcropId,
                targetType: BookmarkTargetType.GigaPan,
                targetId: parseInt(gigaPan.id),
                path: outcropPanoramasTabRoute(outcropId),
              }}
            />
            <PanoramaItemDisplayV4
              pictures={gigaPanPictures(gigaPan)}
              bookmarkable={{
                parentType: BookmarkParentType.GigaPan,
                parentId: parseInt(gigaPan.id),
                path: outcropPanoramasTabRoute(outcropId),
              }}
              refetchQueries={refetchQueries}
            />
          </PublishedContainer>
        </div>
      ))}

      {studies.map(study => (
        <Panel key={study.id}>
          <Panel.Heading className="flex justify-between items-start">
            <Panel.Title>
              <strong>{study.name}</strong>
              <StudyAuthor study={study} />
            </Panel.Title>

            <Link
              to={studyRoute(parseInt(study.id))}
              target="_blank"
              className="btn btn-ghost btn-xs gap-1"
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} /> View Study
            </Link>
          </Panel.Heading>
          <Panel.Body>
            {studyGigaPans(study).map(gigaPan => (
              <div key={gigaPan.id}>
                <PublishedContainer published={isPublished(gigaPan)}>
                  <GigaPanViewer
                    hash={gigaPan.gigaPanHash}
                    bookmarkable={{
                      parentType: BookmarkParentType.GigaPan,
                      parentId: parseInt(study.id),
                      targetType: BookmarkTargetType.GigaPan,
                      targetId: parseInt(gigaPan.id),
                      path: studyPanoramasTabRoute(parseInt(study.id)),
                    }}
                  />
                  <PanoramaItemDisplayV4
                    pictures={gigaPanPictures(gigaPan)}
                    bookmarkable={{
                      parentType: BookmarkParentType.GigaPan,
                      parentId: parseInt(gigaPan.id),
                      path: studyPanoramasTabRoute(parseInt(study.id)),
                    }}
                    refetchQueries={refetchQueries}
                  />
                </PublishedContainer>
              </div>
            ))}
          </Panel.Body>
        </Panel>
      ))}
    </div>
  );
}

function StudyAuthor({ study }: { study: StudyPartsFragment | null }) {
  if (!study?.collectedBy && !study?.collectedYear) {
    return null;
  }

  return (
    <div className="space-x-2">
      {study.collectedBy && <span>{study.collectedBy}</span>}
      {study.collectedYear && (
        <span className="text-muted">{study.collectedYear}</span>
      )}
    </div>
  );
}
